// src/ui/SupportPanel.jsx — what sits where the composer normally is, in the
// official Allo chat.
//
// 🟥 IT REPLACES THE COMPOSER RATHER THAN DISABLING IT. The same choice the
//    group-admin lock makes a hundred lines away in ChatWindow: a greyed-out
//    textarea is a thing to try clicking, and the lock would then have to be
//    enforced a second time on a write the person was invited to attempt. When
//    it is not your turn there is nothing to type into at all.
//
// 🟢 THE STATE COMES FROM THE CHAT DOCUMENT, NOT FROM THIS COMPONENT. Whatever
//    `chats/{id}.support.state` says is what is drawn, so the panel is correct
//    on a second device, after a reload, and the instant a staff reply lands —
//    without this file knowing anything about any of those.

import React, { useRef, useState } from 'react';
import { Lock, Image as ImageIcon, X, Check, Send, ShieldCheck } from './icons';
import {
  chooseTopic, escalate, submitTicket, restartSupport, uploadSupportFile, supportFileKind, supportState,
} from '../services/support';
/* 🖼 a document's first page, a clip's first frame — the facts a card needs to
   be a card rather than a grey icon. See services/attachFacts.js. */
import { factsForSupport } from '../services/attachFacts';
import './support.css';

const MAX_SHOTS = 4;

export default function SupportPanel({ chat, menu, lastAnswer }) {
  const state = supportState(chat);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [body, setBody] = useState('');
  const [shots, setShots] = useState([]);          // { url, name, pct }
  const fileRef = useRef(null);

  const cat = chat?.support?.category || null;
  const asks = lastAnswer?.asks || [];
  const wantsShot = lastAnswer?.wantsScreenshot !== false;

  const run = async (fn) => {
    setBusy(true); setErr('');
    try { await fn(); } catch (e) { setErr(friendly(e)); } finally { setBusy(false); }
  };

  const pick = async (e) => {
    const files = [...(e.target.files || [])].slice(0, MAX_SHOTS - shots.length);
    e.target.value = '';
    for (const f of files) {
      /* 🟥 WHATEVER IT ACTUALLY IS. The picker was `accept="image/*"` and every
         upload was recorded as an image, so this chat could take exactly one
         kind of thing — which is not what somebody with a broken voice note or
         a receipt needs to send. */
      const row = { name: f.name, kind: supportFileKind(f), mime: f.type || null, pct: 0, url: null };
      setShots((s) => [...s, row]);
      try {
        const url = await uploadSupportFile(f, (p) => {
          setShots((s) => s.map((x) => (x === row ? { ...x, pct: p } : x)));
        });
        /* 🖼 🟥 AND EVERYTHING ELSE A RENDERER NEEDS. This sent four fields, so a
           PDF arrived as a grey icon and a clip as a black rectangle — in this
           browser, in the app AND in the staff console — while the identical
           file sent as a broadcast arrived with its first page or its first
           frame showing. `pdfFirstPageThumb` has drawn those for the ordinary
           chat since documents were sendable; this path never asked for one.
           Fail-soft: no facts still sends the file. */
        const facts = await factsForSupport(f, row.kind, (t) => uploadSupportFile(t));
        setShots((s) => s.map((x) => (x === row ? { ...x, url, pct: 1, ...facts } : x)));
      } catch {
        setShots((s) => s.filter((x) => x !== row));
        setErr('That file did not upload. Try again, or send without it.');
      }
    }
  };

  /* ── it is not your turn ───────────────────────────────────────────────── */
  if (state === 'submitted') {
    return (
      <div className="sp-locked">
        <Lock width={14} height={14} />
        <span>
          This is with the Allo team. Only Allo can send messages here until we reply —
          you will get a notification.
        </span>
        <button className="sp-link" disabled={busy}
          onClick={() => run(restartSupport)}>Ask about something else</button>
      </div>
    );
  }

  /* ── the menu ──────────────────────────────────────────────────────────── */
  if (state === 'menu') {
    return (
      <div className="sp">
        {err && <div className="sp-err">{err}</div>}
        {!cat ? (
          <>
            <b className="sp-title">{menu?.title || 'What can we help with?'}</b>
            <p className="sp-note">{menu?.note}</p>
            <div className="sp-grid">
              {(menu?.categories || []).map((c) => (
                <button key={c.id} className={`sp-cat ${c.urgent ? 'urgent' : ''}`} disabled={busy}
                  onClick={() => run(() => chooseTopic(c.id))}>
                  {c.urgent && <ShieldCheck width={14} height={14} />}
                  {c.label}
                </button>
              ))}
            </div>
          </>
        ) : (
          /* 🟢 THE ANSWER CAME FIRST, AND THIS IS THE QUESTION AFTER IT. Most of
             what arrives in a support queue is something the product already
             answers somewhere nobody found — so the answer is shown, and only
             then is there a way to file a ticket. It is one tap away, not
             hidden. */
          <>
            <b className="sp-title">Did that help?</b>
            <div className="sp-row">
              <button className="sp-btn ghost" disabled={busy}
                onClick={() => run(restartSupport)}>
                <Check width={14} height={14} /> Yes, thanks
              </button>
              <button className="sp-btn" disabled={busy}
                onClick={() => run(() => escalate(cat))}>
                No — contact the team
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  /* ── your turn ─────────────────────────────────────────────────────────── */
  const ready = body.trim().length > 1 && !shots.some((s) => s.url === null);

  return (
    <div className="sp compose">
      {err && <div className="sp-err">{err}</div>}

      {asks.length > 0 && (
        <ul className="sp-asks">
          {asks.map((a) => <li key={a}>{a}</li>)}
        </ul>
      )}

      <textarea
        className="sp-text"
        rows={3}
        autoFocus
        value={body}
        maxLength={4000}
        placeholder="Tell us what happened…"
        onChange={(e) => setBody(e.target.value)}
      />

      {shots.length > 0 && (
        <div className="sp-shots">
          {shots.map((s, i) => (
            <div className="sp-shot" key={i}>
              {/* a photo previews; everything else is its name and its kind,
                  because a grey square with a filename under it is more use
                  than a broken <img> for a PDF */}
              {!s.url
                ? <span className="sp-shot-pct">{Math.round((s.pct || 0) * 100)}%</span>
                : s.kind === 'image'
                  ? <img src={s.url} alt="" />
                  : (
                    <span className="sp-shot-file" title={s.name}>
                      <b>{(String(s.name || '').split('.').pop() || '').slice(0, 4).toUpperCase() || 'FILE'}</b>
                      <i>{s.kind}</i>
                    </span>
                  )}
              <button className="sp-shot-x" onClick={() => setShots((x) => x.filter((_, j) => j !== i))}
                title="Remove"><X width={11} height={11} /></button>
            </div>
          ))}
        </div>
      )}

      <div className="sp-row">
        {wantsShot && shots.length < MAX_SHOTS && (
          <>
            <button className="sp-btn ghost" onClick={() => fileRef.current?.click()} disabled={busy}>
              <ImageIcon width={14} height={14} /> Attach a file
            </button>
            <input ref={fileRef} type="file" multiple hidden onChange={pick} />
          </>
        )}
        <button className="sp-btn send" disabled={!ready || busy}
          onClick={() => run(async () => {
            await submitTicket(
              body.trim(),
              /* 🟥 THE FACTS TRAVEL WITH THE URL. Naming four fields here is what
                 threw the first page, the poster, the shape and the duration
                 away AFTER they had been rendered and uploaded — the server's
                 builder reads all of them. */
              shots.filter((s) => s.url).map((s) => ({
                kind: s.kind || 'image',
                url: s.url,
                name: s.name || null,
                mime: s.mime || null,
                size: s.size ?? null,
                width: s.width ?? null,
                height: s.height ?? null,
                thumbUrl: s.thumbUrl ?? null,
                thumbWidth: s.thumbWidth ?? null,
                thumbHeight: s.thumbHeight ?? null,
                durationSec: s.durationSec ?? null,
              })),
            );
            setBody(''); setShots([]);
          })}>
          <Send width={14} height={14} /> {busy ? 'Sending…' : 'Send to Allo'}
        </button>
      </div>

      {/* 🟥 SAID BEFORE THE FILE PICKER OPENS, NOT AFTER. Everywhere else in this
          product a photo is sealed so that nobody but the recipient can open it.
          Here it is not, because the recipient is the person who has to look at
          it. Someone who has been told their photos are end-to-end encrypted is
          owed the exception in words. */}
      <p className="sp-fine">
        <Lock width={11} height={11} />
        This chat is not end-to-end encrypted — the Allo team can read it, which is
        how we answer you. Do not send your password. Anything you attach here can
        be opened by us.
      </p>
    </div>
  );
}

function friendly(e) {
  const m = String(e?.message || '');
  if (m === 'not_your_turn') return 'Allo has not replied yet — you will be able to write again when we do.';
  if (m === 'empty') return 'Write something first.';
  if (m === 'unauthenticated') return 'Your session ended. Sign in again.';
  return 'That did not go through. Try again in a moment.';
}
